// Read rendered pane geometry, never infer the divider from the outer canvas:
// LWC reserves space for its time axis and pane separators.
export function observeChartPaneLayout(chart, container, { onLayout, onRatio }) {
  let disposed = false
  let dragging = false
  let startRatio = null
  let frame = null
  const panes = () => chart.panes()
  const ratio = () => {
    const heights = panes().map(pane => pane.getHeight())
    return heights.length === 2 && heights.every(h => Number.isFinite(h) && h > 0)
      ? heights[0] / (heights[0] + heights[1]) : null
  }
  const publish = () => {
    if (disposed) return
    const height = panes()[0]?.getHeight()
    const axisWidth = chart.priceScale('right').width()
    if (Number.isFinite(height) && height > 0 && Number.isFinite(axisWidth)) {
      onLayout({ height, axisWidth })
    }
  }
  const schedule = () => {
    if (disposed || frame != null) return
    frame = requestAnimationFrame(() => {
      frame = null
      publish()
    })
  }
  const down = event => {
    if (panes().length !== 2) return
    const rect = container.getBoundingClientRect()
    // Convert screen coordinates to CSS pixels, including browser/CSS zoom.
    const scale = container.clientHeight > 0 && rect.height > 0 ? rect.height / container.clientHeight : 1
    const y = (event.clientY - rect.top) / scale
    if (!Number.isFinite(y) || Math.abs(y - panes()[0].getHeight()) > 8) return
    dragging = true
    startRatio = ratio()
  }
  const up = () => {
    if (!dragging) return
    dragging = false
    const next = ratio()
    // Pointerup does not resize the panes; LWC already applied mouse/touch moves.
    // Persist immediately, so a reload need not wait for another animation frame.
    if (next != null && startRatio != null && Math.abs(next - startRatio) > 0.0001) {
      onRatio(Math.min(0.95, Math.max(0.05, next)))
    }
    publish()
  }
  const move = () => { if (dragging) schedule() }
  const observer = new ResizeObserver(schedule)
  observer.observe(container)
  for (const pane of panes()) {
    const element = pane.getHTMLElement?.()
    if (element) observer.observe(element)
  }
  container.addEventListener('pointerdown', down, true)
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', up)
  publish()
  return () => {
    disposed = true
    if (frame != null) cancelAnimationFrame(frame)
    observer.disconnect()
    container.removeEventListener('pointerdown', down, true)
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', up)
  }
}
